'use client';

import {
  Badge,
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '@terra/ui-design-system';
import type {
  AiComponentDto,
  McpIdentityRosterEntryDto,
} from '@terra/shared-contracts-ai-applications';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  getMcpServerAuthMetadata,
  getMcpServerIdentityCapabilities,
  getMcpServerIdentityRoster,
  isMcpAssetComponentType,
} from '../utils/aiComponentMetadata';

type McpServerIdentityRosterProps = {
  component: AiComponentDto;
  assetComponents?: AiComponentDto[];
};

function authStatusColorPalette(
  authStatus: McpIdentityRosterEntryDto['authStatus'],
): 'green' | 'orange' | 'red' | 'gray' {
  switch (authStatus) {
    case 'authorized':
      return 'green';
    case 'insufficient_scope':
      return 'orange';
    case 'auth_required':
      return 'red';
    default:
      return 'gray';
  }
}

function authStatusLabel({
  authStatus,
  t,
}: {
  authStatus: NonNullable<McpIdentityRosterEntryDto['authStatus']> | 'unknown';
  t: (key: string) => string;
}): string {
  switch (authStatus) {
    case 'authorized':
      return t('authStatusAuthorized');
    case 'auth_required':
      return t('authStatusAuthRequired');
    case 'insufficient_scope':
      return t('authStatusInsufficientScope');
    default:
      return t('authStatusUnknown');
  }
}

export function McpServerIdentityRoster({
  component,
  assetComponents = [],
}: McpServerIdentityRosterProps) {
  const t = useTranslations('aiApplications.componentDrawer.mcp');
  const tFields = useTranslations('aiApplications.fields');
  const tIdentities = useTranslations('aiApplications.mcpIdentities');

  if (component.type !== 'MCP_SERVER') {
    return null;
  }

  const auth = getMcpServerAuthMetadata({ metadata: component.metadata });
  const roster = getMcpServerIdentityRoster({ metadata: component.metadata });
  const hasMcpAssets = assetComponents.some((assetComponent) =>
    isMcpAssetComponentType(assetComponent.type),
  );
  const capabilities = hasMcpAssets
    ? getMcpServerIdentityCapabilities({
        serverComponent: component,
        assetComponents,
      })
    : [];

  if (!auth && roster.length === 0 && capabilities.length === 0) {
    return null;
  }

  const scopesSupported = auth?.scopesSupported ?? [];

  return (
    <VStack alignItems="stretch" gap={3}>
      <BodyStrong color="fg.default">{tFields('authSummary')}</BodyStrong>
      <VStack alignItems="stretch" gap={2}>
        {auth ? (
          <VStack alignItems="stretch" gap={1}>
            <HStack gap={1.5} flexWrap="wrap">
              <Badge
                variant="outline"
                colorPalette={auth.requiresAuth ? 'orange' : 'green'}
              >
                {auth.requiresAuth
                  ? t('authStatusAuthRequired')
                  : t('authNotRequired')}
              </Badge>
              {auth.anonAccessible !== undefined ? (
                <Badge
                  variant="outline"
                  colorPalette={auth.anonAccessible ? 'orange' : 'gray'}
                >
                  {auth.anonAccessible
                    ? t('anonAccessible')
                    : t('anonNotAccessible')}
                </Badge>
              ) : null}
            </HStack>
            {scopesSupported.length > 0 ? (
              <MetaDefault color="fg.muted">
                {tIdentities('scopesLabel')}: {scopesSupported.join(', ')}
              </MetaDefault>
            ) : null}
          </VStack>
        ) : null}

        {roster.length > 0 ? (
          <VStack alignItems="stretch" gap={1.5}>
            {roster.length >= 2 ? (
              <MetaDefault color="fg.muted">
                {t('identitiesProbed')}
              </MetaDefault>
            ) : null}
            {roster.map((entry) => {
              const authStatus = entry.authStatus ?? 'unknown';
              const grantedScopes = entry.grantedScopes ?? [];
              const missingServerScopes = entry.missingServerScopes ?? [];
              const challengeScopes = entry.challengeScopes ?? [];
              const hasDetails =
                grantedScopes.length > 0 ||
                missingServerScopes.length > 0 ||
                challengeScopes.length > 0 ||
                Boolean(entry.error);

              return (
                <VStack
                  key={entry.identityId}
                  alignItems="stretch"
                  gap={0.5}
                  minW={0}
                >
                  <HStack
                    justifyContent="space-between"
                    alignItems="center"
                    gap={3}
                    minH={8}
                  >
                    <BodyDefault color="fg.default" truncate>
                      {entry.label}
                    </BodyDefault>
                    <HStack gap={1.5} flexShrink={0}>
                      <Badge
                        variant="outline"
                        colorPalette={authStatusColorPalette(authStatus)}
                      >
                        {authStatusLabel({ authStatus, t })}
                      </Badge>
                      {!entry.connectionSuccess ? (
                        <Badge colorPalette="red" variant="outline">
                          {t('connectionFailed')}
                        </Badge>
                      ) : null}
                    </HStack>
                  </HStack>
                  {hasDetails ? (
                    <VStack alignItems="stretch" gap={0.5} pl={0}>
                      {grantedScopes.length > 0 ? (
                        <MetaDefault color="fg.muted">
                          {t('grantedScopes')}: {grantedScopes.join(', ')}
                        </MetaDefault>
                      ) : null}
                      {missingServerScopes.length > 0 ? (
                        <MetaDefault color="fg.muted">
                          {t('missingServerScopes')}:{' '}
                          {missingServerScopes.join(', ')}
                        </MetaDefault>
                      ) : null}
                      {challengeScopes.length > 0 ? (
                        <MetaDefault color="fg.muted">
                          {t('challengeScopes')}: {challengeScopes.join(', ')}
                        </MetaDefault>
                      ) : null}
                      {entry.error ? (
                        <MetaDefault color="fg.error">
                          {entry.error}
                        </MetaDefault>
                      ) : null}
                    </VStack>
                  ) : null}
                </VStack>
              );
            })}
          </VStack>
        ) : null}

        {capabilities.length > 0 ? (
          <VStack alignItems="stretch" gap={1}>
            <MetaDefault color="fg.muted">{t('identityAccess')}</MetaDefault>
            {capabilities.map((capability) => (
              <MetaDefault key={capability.identityId} color="fg.default">
                {capability.label}:{' '}
                {capability.accessDenied
                  ? t('notListed')
                  : capability.assetNames.join(', ')}
              </MetaDefault>
            ))}
          </VStack>
        ) : null}
      </VStack>
    </VStack>
  );
}
